import { season } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type {
	UpdateEvents,
	ViewInput,
	PlayoffSeries,
} from "../../../common/types.ts";

type SeriesTeam = {
	abbrev: string;
	cid: number;
	imgURL?: string;
	imgURLSmall?: string;
	pendingPlayIn?: true;
	pts?: number;
	sPts?: number;
	region: string;
	regularSeason: {
		won: number;
		lost: number;
		tied?: number;
		otl?: number;
	};
	seed: number;
	tid: number;
	winp: number;
	won?: number;
};

type PlayInMatchup = {
	home: SeriesTeam;
	away: SeriesTeam;
};

type PlayIns =
	| (
			| [PlayInMatchup, PlayInMatchup]
			| [PlayInMatchup, PlayInMatchup, PlayInMatchup]
	  )[]
	| undefined;

const updatePlayoffs = async (
	inputs: ViewInput<"playoffs">,
	updateEvents: UpdateEvents,
	state: any,
): Promise<
	| {
			confNames: string[];
			finalMatchups: boolean;
			matchups: {
				matchup: [number, number];
				rowspan: number;
			}[][];
			numGamesPlayoffSeries: number[];
			numGamesToWinSeries: number[];
			playIns: PlayIns;
			playoffsByConf: number | false;
			season: number;
			series: {
				home: SeriesTeam;
				away?: SeriesTeam;
			}[][];
			userTid: number;
	  }
	| undefined
> => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("playoffs") ||
		inputs.season !== state.season ||
		(inputs.season === g.get("season") && updateEvents.includes("gameSim"))
	) {
		let finalMatchups = false;
		let series: PlayoffSeries["series"];
		let playIns: PlayoffSeries["playIns"];

		const playoffSeries = await idb.getCopy.playoffSeries({
			season: inputs.season,
		});

		if (playoffSeries) {
			series = playoffSeries.series;
			playIns = playoffSeries.playIns;
			finalMatchups = true;
		} else {
			const result = await season.genPlayoffSeries();
			series = result.series;
			playIns = result.playIns;
		}

		await helpers.augmentSeries(series, inputs.season);

		if (playIns) {
			await helpers.augmentSeries(playIns, inputs.season);
		}

		// Because augmentSeries mutates series, this is for TypeScript
		const series2 = series as {
			home: SeriesTeam;
			away?: SeriesTeam;
		}[][];
		const playIns2 = playIns as PlayIns;

		// Formatting for the table in playoffs.html
		const matchups: {
			rowspan: number;
			matchup: [number, number];
		}[][] = [];

		for (let i = 0; i < 2 ** (series.length - 2); i++) {
			matchups[i] = [];
		}

		// Fill in with each round. Good lord, this is confusing, due to having to assemble it for an HTML table with rowspans.
		for (let i = 0; i < series.length; i++) {
			let numGamesInSide = 2 ** (series.length - i - 2);

			if (numGamesInSide < 1) {
				numGamesInSide = 1;
			}

			const rowspan = 2 ** i;

			for (let j = 0; j < numGamesInSide; j++) {
				matchups[j * rowspan]!.splice(i, 0, {
					rowspan,
					matchup: [i, j],
				});

				if (series.length !== i + 1) {
					matchups[j * rowspan]!.splice(i, 0, {
						rowspan,
						matchup: [i, numGamesInSide + j],
					});
				}
			}
		}

		const confNames = g.get("confs", inputs.season).map((conf) => conf.name); // Display the current or archived playoffs

		const numGamesPlayoffSeries = g.get("numGamesPlayoffSeries", inputs.season);

		const playoffsByConf = await season.getPlayoffsByConf(inputs.season);

		return {
			confNames,
			finalMatchups,
			matchups,
			numGamesPlayoffSeries,
			numGamesToWinSeries: numGamesPlayoffSeries.map(
				helpers.numGamesToWinSeries,
			),
			playIns: playIns2,
			playoffsByConf,
			season: inputs.season,
			series: series2,
			userTid: g.get("userTid"),
		};
	}
};

export default updatePlayoffs;
